// Fraîcheur du suivi « Google Sites » (google-sites-install B4_01, D44) — helpers PURS, testés dans
// `src/tests/hostingFreshness.test.js` (D38 : aucun outil de test de rendu dans ce repo).
//
// Gabarit : `websiteActivity.js`. Les états sont volontairement renommés (`fresh|stale` au lieu
// de `active|inactive`) : la fraîcheur d'un constat n'est pas l'activité d'un site.
//
// Trois états, jamais deux (super-admin/CLAUDE.md, piège « champ d'API pas encore déployé ») :
//  - `undefined` (champ ABSENT de la réponse) → `unknown` : l'API en face ne sert pas encore
//    `last_full_analysis_at` (super-admin déployé depuis `master`, API depuis `dev`). Jamais fondu
//    dans un état métier.
//  - `null` → `never` : aucun passage COMPLET du parc n'a encore abouti (le backfill n'a pas
//    tourné, ou un site échoue à chaque passage — D44 strict). ⛔ Jamais un bandeau d'ancienneté
//    calculé sur `null`.
//  - date → `fresh` (≤ 30 j) ou `stale` (> 30 j : bandeau « chiffres périmés »).
// La date servie est le PLANCHER de fraîcheur du parc (`min(hosting_analyzed_at)`), jamais
// « maintenant » : elle vieillit d'elle-même tant qu'une partie du parc n'est pas rejouée.

export const STALE_AFTER_DAYS = 30;

// Une « dernière discussion cassée » plus vieille que cette fenêtre est une cicatrice, pas une
// panne vive (D63 : la liste est un stock cumulé, un site réparé n'en sort jamais). Décision
// Mathias 2026-09-20 (locale, 🛑1) : les lignes concernées restent listées mais sont grisées.
export const OLD_BREAK_AFTER_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

function daysSince(value, now) {
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

export function hostingFreshness(lastFullAnalysisAt, now = new Date()) {
  if (lastFullAnalysisAt === undefined) {
    return { state: 'unknown', days: null, label: '—' };
  }
  if (lastFullAnalysisAt === null || lastFullAnalysisAt === '') {
    return { state: 'never', days: null, label: 'Aucune analyse complète à ce jour' };
  }
  const days = daysSince(lastFullAnalysisAt, now);
  if (days === null) {
    // Date illisible : on ne sait pas — surtout pas « aucune analyse » (affirmation métier fausse).
    return { state: 'unknown', days: null, label: '—' };
  }
  const when = days === 0 ? 'aujourd’hui' : `il y a ${days} j`;
  if (days > STALE_AFTER_DAYS) {
    return { state: 'stale', days, label: `Dernière analyse complète : ${when}` };
  }
  return { state: 'fresh', days, label: `Dernière analyse complète : ${when}` };
}

// Part des discussions cassées sans aucun commentaire, en % entier de `threads_total`.
// `threads_total > 0` est la condition même du marqueur ; on rend `null` (pas 0) si la donnée
// est absente ou incohérente, pour que l'écran affiche « — » plutôt qu'un faux 0 %.
export function emptyRatio(threadsEmpty, threadsTotal) {
  const total = Number(threadsTotal);
  const empty = Number(threadsEmpty);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(empty)) return null;
  return Math.round((Math.max(0, empty) / total) * 100);
}

// Une ligne dont la dernière discussion cassée date de plus de OLD_BREAK_AFTER_DAYS est une
// cicatrice. `last_thread_at` absent/illisible → { old: false, days: null } : on ne grise pas
// ce qu'on ne sait pas dater.
export function breakAge(lastThreadAt, now = new Date()) {
  if (lastThreadAt === undefined || lastThreadAt === null || lastThreadAt === '') {
    return { old: false, days: null };
  }
  const days = daysSince(lastThreadAt, now);
  if (days === null) return { old: false, days: null };
  return { old: days > OLD_BREAK_AFTER_DAYS, days };
}
